import { useCallback, useState } from "react";

/**
 * The confirm -> save -> report cycle shared by the admin create/edit screens.
 *
 * Every one of them wants the same four pieces of state (what is waiting behind the
 * dialog, whether the request is in flight, why it failed, what was written) wired the
 * same way, and getting the order wrong is what makes a save feel untrustworthy: the
 * dialog must stay up until the server answers, so that it closing is itself the signal
 * the write landed rather than that it was merely sent.
 *
 *   const save = useSaveConfirmation({
 *       save: () => dispatch(addVehicle(formData)),
 *       onDismiss: () => history.push("/admin/vehiclelist"),
 *   });
 *   ...
 *   <ConfirmationModal show={!!save.summary} isBusy={save.isSaving} error={save.error}
 *       onCancel={save.cancel} onConfirm={save.confirm}>
 *       <ValueList rows={save.summary || []} />
 *   </ConfirmationModal>
 *   <SuccessModal show={!!save.saved} onClose={save.dismiss}>...</SuccessModal>
 *
 * `save` must resolve to `{ ok, message }` — the admin actions do. A rejected save
 * leaves the dialog open carrying `message`, because closing it would drop the admin
 * back on a form still showing values that were never written, with nothing to say so.
 */
export const useSaveConfirmation = ({ save, onDismiss }) => {
    // The summary waiting behind the confirmation dialog. Non-null means it is open.
    const [summary, setSummary] = useState(null);
    // The summary the server accepted, kept so the success dialog can name it.
    const [saved, setSaved] = useState(null);
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const request = useCallback((nextSummary) => {
        setError("");
        setSummary(nextSummary);
    }, []);

    const cancel = useCallback(() => {
        setSummary(null);
        setError("");
    }, []);

    const confirm = useCallback(async () => {
        setIsSaving(true);
        setError("");

        const result = await save();

        setIsSaving(false);

        if (!result?.ok) {
            setError(result?.message || "This could not be saved. Please try again.");
            return;
        }

        setSaved(summary);
        setSummary(null);
    }, [save, summary]);

    const dismiss = useCallback(() => {
        setSaved(null);
        onDismiss?.();
    }, [onDismiss]);

    return { summary, saved, error, isSaving, request, cancel, confirm, dismiss };
};

export default useSaveConfirmation;
